import { fr } from "@codegouvfr/react-dsfr"
import Button from "@codegouvfr/react-dsfr/Button"
import RadioButtons from "@codegouvfr/react-dsfr/RadioButtons"
import { Box, CircularProgress, FormControl, FormHelperText, FormLabel, Input, type InputProps, TextareaAutosize, Typography } from "@mui/material"
import type { Result } from "email-misspelled"
import emailMisspelled, { top100 } from "email-misspelled"
import { useFormik } from "formik"
import type { ChangeEvent } from "react"
import { useRef, useState } from "react"
import type { ILbaItemLbaCompanyJson, ILbaItemLbaJobJson, ILbaItemPartnerJobJson } from "shared"
import { LBA_ITEM_TYPE, LBA_ITEM_TYPE_OLD } from "shared/constants/lbaitem"
import dayjs from "shared/helpers/dayjs"
import { APPLICANT_ANSWER_MAX_LENGTH } from "shared/models/applications.model"
import { toFormikValidationSchema } from "zod-formik-adapter"
import { MultiSelectField } from "@/app/_components/FormComponents/MultiSelectField"
import { SelectField } from "@/app/_components/FormComponents/SelectField"
import { LiveStatus } from "@/app/_components/LiveStatus"
import ModalCloseButton from "@/app/_components/ModalCloseButton"
import { ModalTitle } from "@/app/_components/Title/ModalTitle"
import { DsfrLink } from "@/components/dsfr/DsfrLink"
import InfoBanner from "@/components/InfoBanner/InfoBanner"
import { sessionStorageGet, sessionStorageSet } from "@/utils/local-storage"
import { CandidatureLbaFileDropzone } from "./CandidatureLbaFileDropzone"
import type { IApplicationSchemaInitValues } from "./services/get-schema"
import { ApplicationFormikSchema, formatCount, getInitialSchemaValues, QuestionAnswerSchema } from "./services/get-schema"

const emailChecker = emailMisspelled({ maxMisspelled: 3, domains: top100 })

type FormikType = ReturnType<typeof useFormik>

export const CandidatureLbaModalBody = ({
  isLoading,
  company,
  item,
  kind,
  fromWidget = false,
  onSubmit,
  onClose,
}: {
  isLoading: boolean
  company: string
  item: ILbaItemLbaJobJson | ILbaItemLbaCompanyJson | ILbaItemPartnerJobJson
  kind: LBA_ITEM_TYPE | LBA_ITEM_TYPE_OLD
  fromWidget?: boolean
  onSubmit: (values: IApplicationSchemaInitValues) => void
  onClose: () => void
}) => {
  const customQuestions = "job" in item ? (item.job?.to_applicant_questions ?? []) : []

  // formik interprète le nom d'un champ comme un chemin lodash : un intitulé de question contenant « . » ou « [ »
  // écrirait la saisie dans une propriété imbriquée, laissant le textarea bloqué sur sa valeur initiale.
  // on utilise donc un nom de champ indexé, et on rétablit l'intitulé de la question à la soumission.
  const questionFields = customQuestions.map((question, index) => ({ question, name: `custom_question_${index}` }))

  const questionZodExtension = Object.fromEntries(questionFields.map(({ name }) => [name, QuestionAnswerSchema]))

  const applicantAnswersSession = JSON.parse(sessionStorageGet("application-form-answers"))
  const questionsInitValues = Object.fromEntries(questionFields.map(({ question, name }) => [name, applicantAnswersSession?.[question] ?? ""]))

  const formik = useFormik({
    initialValues: { ...getInitialSchemaValues(), ...questionsInitValues },
    validationSchema: toFormikValidationSchema(ApplicationFormikSchema.extend(questionZodExtension)),
    onSubmit: (values: any) => {
      const finalValues: IApplicationSchemaInitValues = { ...values }
      finalValues.applicant_answers_to_recruiter_questions = []
      const savedAnswers = {}

      questionFields.forEach(({ question, name }) => {
        delete finalValues[name]
        const answer = values[name]
        finalValues.applicant_answers_to_recruiter_questions.push({
          question,
          answer,
        })
        savedAnswers[question] = answer
      })
      sessionStorageSet("application-form-answers", savedAnswers)
      onSubmit(finalValues)
    },
  })

  const isOffre =
    kind === LBA_ITEM_TYPE.OFFRES_EMPLOI_LBA || kind === LBA_ITEM_TYPE.OFFRES_EMPLOI_PARTENAIRES || kind === LBA_ITEM_TYPE_OLD.MATCHA || kind === LBA_ITEM_TYPE_OLD.PARTNER_JOB

  const handleSubmitWithScrollOnFirstError = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const errors = await formik.validateForm()

    if (Object.keys(errors).length > 0) {
      await formik.setTouched(Object.fromEntries(Object.keys(errors).map((k) => [k, true])))
      const errorElements = Object.keys(errors)
        .map((field) => document.getElementById(field) ?? document.querySelector<HTMLElement>(`[name="${field}"]`))
        .filter(Boolean) as HTMLElement[]
      const firstEl = errorElements.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))[0]
      firstEl?.scrollIntoView({ behavior: "smooth", block: "center" })
      firstEl?.focus()
      return
    }

    formik.submitForm()
  }

  return (
    <Box
      sx={{
        ".MuiFormLabel-asterisk": {
          color: "#ce0500",
        },
      }}
    >
      <form noValidate onSubmit={handleSubmitWithScrollOnFirstError} style={{ position: "relative" }}>
        <Box sx={{ margin: { xs: fr.spacing("4v"), md: fr.spacing("6v") }, mb: 0 }}>
          {!fromWidget && (
            <>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mr: -6, mb: fr.spacing("4v") }}>
                <ModalCloseButton onClose={onClose} />
              </Box>
            </>
          )}
          <ModalTitle sx={{ mb: fr.spacing("4v") }}>
            {isOffre ? (
              <>
                Postuler à l&apos;offre {fromWidget ? `${item.title} ` : ""}de {company}
              </>
            ) : (
              <>Candidature spontanée{fromWidget ? ` auprès de ${company}` : ""}</>
            )}
          </ModalTitle>

          <Typography sx={{ mb: 2, fontSize: "14px", color: "grey.600", my: fr.spacing("2v") }}>Les champs marqués d’un astérisque (*) sont obligatoires.</Typography>

          <Box sx={{ pt: fr.spacing("3v"), display: "flex", gap: fr.spacing("6v"), flexDirection: { xs: "column", md: "row" } }}>
            <Box
              sx={(theme) => ({
                height: "fit-content",
                [theme.breakpoints.up("xs")]: {},
                [theme.breakpoints.up("md")]: {
                  backgroundColor: "#F6F6F6",
                  width: 484,
                  px: fr.spacing("6v"),
                  py: fr.spacing("8v"),
                },
              })}
            >
              <MesInformations formik={formik} />
              <MaRechercheDAlternance formik={formik} />
            </Box>
            <Box sx={{ flex: 1, px: fr.spacing("2v"), pt: { xs: 0, md: fr.spacing("2v") } }}>
              <ModalTitle component="h2" sx={{ mb: fr.spacing("3v"), color: "#000091" }}>
                Mon message personnalisé
              </ModalTitle>
              <Box sx={{ mb: fr.spacing("3v") }}>
                <InfoText>Un message personnalisé augmente vos chances d'obtenir un contact avec le recruteur.</InfoText>
              </Box>
              <TextareaInput formik={formik} name="applicant_message" label="Message au responsable du recrutement" />
              <Box sx={{ mt: fr.spacing("4v") }}>
                <CvFileInput formik={formik} />
              </Box>
              {customQuestions.length > 0 && (
                <>
                  <ModalTitle
                    component="h2"
                    sx={{
                      mb: fr.spacing("3v"),
                      color: "#000091",
                    }}
                  >
                    Mes réponses aux questions de l’entreprise
                  </ModalTitle>
                  <Box sx={{ mb: fr.spacing("3v") }}>
                    <InfoText>L’entreprise sera attentive aux réponses que vous lui apporterez. Prenez le temps de détailler vos réponses pour le recruteur.</InfoText>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: fr.spacing("6v") }}>
                    {questionFields.map(({ question, name }) => (
                      <TextareaInput key={name} formik={formik} name={name} label={question} maxLength={APPLICANT_ANSWER_MAX_LENGTH} required />
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={(theme) => ({
            padding: fr.spacing("6v"),
            backgroundColor: "white",
            [theme.breakpoints.up("xs")]: {},
            [theme.breakpoints.up("md")]: {
              position: "sticky",
              boxShadow: "0 6px 18px 0 #00001229",
              bottom: 0,
              left: 0,
            },
          })}
        >
          <InfoBanner showInfo={false} showAlert={false} showOK={false} forceEnvBanner={true} />
          <Box sx={{ display: "flex", gap: fr.spacing("6v"), alignItems: { xs: "flex-end", md: "center" }, flexDirection: { xs: "column", md: "row" } }}>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "24px",
                a: {
                  fontSize: "14px !important",
                },
              }}
            >
              En remplissant ce formulaire, vous acceptez les{" "}
              <DsfrLink href="/conditions-generales-utilisation" external>
                Conditions générales d&apos;utilisation
              </DsfrLink>{" "}
              du service La bonne alternance et acceptez le partage de vos informations avec l&apos;établissement {company}. Pour plus d'informations sur le traitement de vos
              données à caractère personnel, veuillez consulter la{" "}
              <DsfrLink href="/politique-de-confidentialite" external>
                Politique de confidentialité
              </DsfrLink>{" "}
              de La bonne alternance.
            </Typography>

            {isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row" }} data-testid="candidature-currently-sending">
                <CircularProgress sx={{ mr: fr.spacing("8v") }} />
                <Typography>Veuillez patienter</Typography>
              </Box>
            ) : isOffre ? (
              <Button
                iconId="fr-icon-arrow-right-line"
                iconPosition="left"
                data-tracking-id="postuler-offre-lba"
                type="submit"
                data-testid="candidature-not-sent"
                style={{ minWidth: "fit-content" }}
              >
                J'envoie ma candidature
              </Button>
            ) : (
              <Button
                iconId="fr-icon-arrow-right-line"
                iconPosition="left"
                data-tracking-id="postuler-entreprise-algo"
                type="submit"
                data-testid="candidature-not-sent"
                style={{ minWidth: "fit-content" }}
              >
                J'envoie ma candidature spontanée
              </Button>
            )}
          </Box>
        </Box>
      </form>
    </Box>
  )
}

const MesInformations = ({ formik }: { formik: FormikType }) => {
  return (
    <>
      <ModalTitle component="h2" sx={{ mb: fr.spacing("6v") }}>
        Mes informations
      </ModalTitle>
      <Box sx={{ display: "flex", flexDirection: "column", gap: fr.spacing("6v") }}>
        <FormikInput name="applicant_last_name" label="Nom" formik={formik} required inputProps={{ autoComplete: "family-name" }} />
        <FormikInput name="applicant_first_name" label="Prénom" formik={formik} required inputProps={{ autoComplete: "given-name" }} />
        <EmailInput formik={formik} />
        <FormikInput
          name="applicant_phone"
          label="Téléphone"
          hint="Par exemple 0612345678 ou +33612345678"
          formik={formik}
          required
          inputProps={{
            type: "tel",
            autoComplete: "tel",
            sx: { mt: { xs: 3, md: 0 } },
          }}
        />
      </Box>
    </>
  )
}

const firstCharUppercase = (str: string): string => str.charAt(0).toUpperCase() + str.substring(1)

const MaRechercheDAlternance = ({ formik }: { formik: FormikType }) => {
  const applicant_inscription_formation = formik.values.applicant_inscription_formation as boolean | undefined

  const contractStartLabels = ["Dès que possible", ...new Array(12).fill(0).map((_, monthIndex) => `${firstCharUppercase(dayjs().add(monthIndex, "months").format("MMMM YYYY"))}`)]

  return (
    <>
      <ModalTitle component="h2" sx={{ my: fr.spacing("3v") }}>
        Ma recherche d'alternance
      </ModalTitle>
      <Box sx={{ mb: fr.spacing("3v") }}>
        <InfoText>Ces informations aident l’entreprise à étudier votre candidature. La taille des champs est limitée à 200 caractères.</InfoText>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: fr.spacing("6v") }}>
        <RadioInput
          formik={formik}
          name="applicant_inscription_formation"
          label="Ma situation"
          options={[
            { label: "Je suis déjà inscrit(e) en formation", value: true },
            { label: "Je ne suis pas encore inscrit(e) en formation", value: false },
          ]}
        />
        {applicant_inscription_formation !== undefined && (
          <>
            <FormikSelect
              formik={formik}
              name="applicant_contract_duration"
              label="Durée du contrat souhaitée"
              emptyButtonHint="Vider la durée du contrat souhaitée"
              options={[6, 12, 18, 24, 36].map((len) => `${len} mois`)}
            />
            <MultiSelect name="applicant_contract_start" label="Début de contrat souhaité" formik={formik} options={contractStartLabels} />
          </>
        )}
        {applicant_inscription_formation === true && (
          <>
            <TextareaInput
              formik={formik}
              name="applicant_formation_description"
              label="Informations sur l’école et la formation"
              description="Ex: Nom de l’école / Intitulé de la formation / Ville"
            />
            <TextareaInput
              formik={formik}
              name="applicant_rythm_description"
              label="Rythme de l’alternance (école/entreprise)"
              description="Ex: 1 semaine à l’école / 2 semaines en entreprise"
            />
          </>
        )}
      </Box>
    </>
  )
}

const EmailInput = ({ formik }: { formik: FormikType }) => {
  const [suggestedEmails, setSuggestedEmails] = useState<Result[]>([])
  const emailInputRef = useRef<HTMLInputElement>(null)

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checkedEmail = emailChecker(e.target.value)
    setSuggestedEmails(checkedEmail)
    formik.handleChange(e)
  }

  const clickSuggestion = (value: string) => {
    formik.setFieldValue("applicant_email", value)
    setSuggestedEmails([])
    // Le bouton cliqué disparaît avec les suggestions : sans cela, la modale renvoie le focus sur « Fermer ».
    emailInputRef.current?.focus()
  }

  return (
    <FormikInput
      formik={formik}
      label="E-mail"
      name="applicant_email"
      hint="Format attendu : nom@domaine.fr"
      required
      inputProps={{
        type: "email",
        autoComplete: "email",
        onChange: onEmailChange,
        inputRef: emailInputRef,
      }}
      postInput={
        <>
          {suggestedEmails.length > 0 && (
            <Box sx={{ mt: 2, fontSize: "12px", color: "grey.600" }}>
              <Typography component="span" sx={{ mr: fr.spacing("4v") }}>
                Voulez-vous dire ?
              </Typography>
              {suggestedEmails.map((suggestedEmail) => (
                <Button key={suggestedEmail.corrected} type="button" onClick={() => clickSuggestion(suggestedEmail.corrected)} priority="tertiary no outline" size="small">
                  {suggestedEmail.corrected}
                </Button>
              ))}
            </Box>
          )}
          <LiveStatus message={suggestedEmails.length > 0 ? `Voulez-vous dire ${suggestedEmails.map(({ corrected }) => corrected).join(" ou ")} ?` : ""} />
        </>
      }
    />
  )
}

const FormikInput = ({
  name,
  label,
  hint,
  required,
  formik,
  inputProps,
  postInput,
}: {
  name: string
  label: string
  hint?: string
  required?: boolean
  formik: FormikType
  inputProps?: Partial<InputProps>
  postInput?: React.ReactNode
}) => {
  const value = formik.values[name]
  const touched = formik.touched[name]
  const error = formik.errors[name] as string
  const displayedErrorOpt = touched && error
  const hintId = `${name}-hint`
  const errorId = `${name}-error`

  return (
    // required sur le FormControl : Input le pose sur l'<input>, l'astérisque du FormLabel étant aria-hidden
    <FormControl required={required} error={Boolean(displayedErrorOpt)} fullWidth>
      <FormLabel htmlFor={name}>{label}</FormLabel>
      {hint && <HintText id={hintId}>{hint}</HintText>}
      <Input
        fullWidth={true}
        data-testid={name}
        id={name}
        name={name}
        type="text"
        error={Boolean(displayedErrorOpt)}
        aria-describedby={describedBy(hint && hintId, displayedErrorOpt && errorId)}
        value={value}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className={fr.cx("fr-input")}
        {...inputProps}
      />
      {postInput}
      <FormHelperText id={errorId}>{displayedErrorOpt}</FormHelperText>
    </FormControl>
  )
}

const FormikSelect = ({ formik, name, label, options, emptyButtonHint }: { formik: FormikType; name: string; label: string; options: string[]; emptyButtonHint: string }) => {
  const value = formik.values[name]
  const touched = formik.touched[name]
  const error = formik.errors[name] as string
  const displayedErrorOpt = touched && error

  return (
    <Box sx={{ display: "flex", gap: fr.spacing("3v"), flexDirection: "column" }}>
      <SelectField
        id={name}
        label={label}
        style={{
          marginBottom: 0,
          textWrap: "nowrap",
        }}
        options={options.map((option) => ({ value: option, label: option, selected: option === value }))}
        nativeSelectProps={{
          value: value ?? undefined,
          onChange: (event) => {
            const { value } = event.target
            const option = options.find((option) => option === value)
            formik.setFieldValue(name, option, true)
          },
          style: {
            fontWeight: 700,
          },
        }}
        state={displayedErrorOpt ? "error" : "default"}
        stateRelatedMessage={displayedErrorOpt}
      />
      {/* RGAA 7.1 : réinitialiser un champ est une action, pas un lien (un <a href="#"> renverrait
          en haut de page au clavier). type="button" évite en plus de soumettre le formulaire. */}
      <button
        type="button"
        onClick={() => formik.setFieldValue(name, null, true)}
        style={{
          textUnderlinePosition: "under",
          height: "fit-content",
          alignSelf: "flex-end",
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
        }}
        className={fr.cx("fr-link")}
      >
        Réinitialiser
        <span className="fr-sr-only"> - {emptyButtonHint}</span>
      </button>
    </Box>
  )
}

const CvFileInput = ({ formik }: { formik: FormikType }) => {
  const setFileValue = (fileValue) => {
    formik.setFieldValue("applicant_attachment_name", fileValue?.applicant_attachment_name || null)
    formik.setFieldValue("applicant_attachment_content", fileValue?.applicant_attachment_content || null)
  }

  return <CandidatureLbaFileDropzone formik={formik} setFileValue={setFileValue} />
}

const TextareaInput = ({
  formik,
  name,
  label,
  required,
  description,
  maxLength,
  infoText,
}: {
  formik: FormikType
  name: string
  label: string
  required?: boolean
  description?: string
  /** Affiche la limite avant la saisie et un compteur sous le champ, sans bloquer la frappe : le dépassement passe par la validation. */
  maxLength?: number
  infoText?: string
}) => {
  const value = formik.values[name]
  const touched = formik.touched[name]
  const error = formik.errors[name] as string
  const displayedErrorOpt = touched && error
  const hintId = `${name}-hint`
  const counterId = `${name}-counter`
  const errorId = `${name}-error`
  const hint = maxLength !== undefined ? [description, `${formatCount(maxLength)} caractères maximum`].filter(Boolean).join(". ") : description
  const length = value?.length ?? 0
  const isOverLimit = maxLength !== undefined && length > maxLength

  return (
    <FormControl required={required} error={Boolean(displayedErrorOpt)} fullWidth>
      <FormLabel htmlFor={name} sx={{ mb: fr.spacing("1v"), fontSize: "16px", lineHeight: "24px" }}>
        {label}
      </FormLabel>
      {hint && <HintText id={hintId}>{hint}</HintText>}
      <TextareaAutosize
        style={{
          marginTop: fr.spacing("2v"),
          ...(displayedErrorOpt ? { borderColor: "#ce0500" } : {}),
        }}
        className={fr.cx("fr-input")}
        id={name}
        data-testid={name}
        name={name}
        onBlur={formik.handleBlur}
        onChange={formik.handleChange}
        value={value}
        // TextareaAutosize n'est pas un InputBase : ni l'état d'erreur ni required du FormControl ne lui parviennent
        required={required}
        aria-invalid={Boolean(displayedErrorOpt)}
        aria-describedby={describedBy(hint && hintId, maxLength !== undefined && counterId, displayedErrorOpt && errorId)}
      />
      {maxLength !== undefined ? (
        <>
          {/* wrap-reverse : l'erreur partage la ligne du compteur, et passe dessous quand la place manque. */}
          <Box sx={{ display: "flex", flexWrap: "wrap-reverse", alignItems: "baseline", columnGap: fr.spacing("4v"), mt: fr.spacing("1v") }}>
            <FormHelperText id={errorId} sx={{ m: 0 }}>
              {displayedErrorOpt}
            </FormHelperText>
            <Typography id={counterId} sx={{ ml: "auto", fontSize: "12px", lineHeight: "20px", color: isOverLimit ? "#ce0500" : "grey.600" }}>
              {formatCount(length)} {length > 1 ? "caractères" : "caractère"} sur {formatCount(maxLength)}
            </Typography>
          </Box>
          {/* Annonce le passage de la limite pendant la frappe, l'erreur formik n'apparaissant qu'à la sortie du champ. */}
          <LiveStatus message={isOverLimit ? `Votre réponse dépasse ${formatCount(maxLength)} caractères.` : ""} />
        </>
      ) : (
        <FormHelperText id={errorId}>{displayedErrorOpt}</FormHelperText>
      )}
      {Boolean(infoText) && (
        <Box sx={{ mt: fr.spacing("6v") }}>
          <InfoText>{infoText}</InfoText>
        </Box>
      )}
    </FormControl>
  )
}

const RadioInput = <T extends { label: string; value: any }>({
  formik,
  name,
  options,
  label,
  infoText,
}: {
  formik: FormikType
  name: string
  options: T[]
  label: string
  infoText?: string
}) => {
  const value = formik.values[name]
  const touched = formik.touched[name]
  const error = formik.errors[name] as string
  const displayedErrorOpt = touched && error

  return (
    <Box>
      <RadioButtons
        style={{
          marginBottom: 0,
        }}
        name={name}
        legend={label}
        options={options.map((option) => ({
          label: option.label,
          nativeInputProps: {
            checked: value === option.value,
            onChange: () => {
              formik.setFieldValue(name, option.value, true)
            },
          },
        }))}
        state={displayedErrorOpt ? "error" : "default"}
        stateRelatedMessage={displayedErrorOpt ? `${error}` : undefined}
      />
      <InfoText>{infoText}</InfoText>
    </Box>
  )
}

const describedBy = (...ids: (string | false | undefined)[]) => ids.filter(Boolean).join(" ") || undefined

const HintText = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={id} sx={{ mb: fr.spacing("1v"), fontSize: "12px", lineHeight: "20px", color: "grey.600" }}>
    {children}
  </Typography>
)

const InfoText = ({ children }: { children: React.ReactNode }) => {
  if (!children) {
    return null
  }
  return (
    <Box sx={{ marginTop: 0, display: "flex", gap: "4px", alignItems: "flex-start", color: "var(--text-default-info)" }}>
      <Box sx={{ mt: "2px !important" }} className="fr-info-text" />
      <Typography sx={{ fontSize: "12px", lineHeight: "20px" }}>{children}</Typography>
    </Box>
  )
}

const MultiSelect = ({ formik, name, label, options }: { formik: FormikType; name: string; label: string; options: string[] }) => {
  const value = formik.values[name] || []

  return (
    <MultiSelectField
      id={name}
      label={label}
      options={options.map((label) => ({ value: label, label }))}
      value={value}
      onChange={() => undefined}
      onConfirm={(newValue) => {
        const orderedValues = options.filter((option) => newValue.includes(option))
        formik.setFieldValue(name, orderedValues, true)
      }}
      onOpen={() => undefined}
      getLabel={(selected) => (selected.length ? selected.map((option) => option.label).join(", ") : "Sélectionner une ou plusieurs options")}
      popperSx={{ width: "425px", maxWidth: { xs: "100%", md: "512px" } }}
    />
  )
}
